import React from 'react';
import PropTypes from 'prop-types';
import Table from 'semantic-table-grid';
import styled from 'styled-components';
import { Accordion, Divider, Icon, Label, Message } from 'semantic-ui-react';
import moment from 'moment';
import _ from 'lodash';

// Services
import GestioneProcessoImprese from 'shared/services/rpc/gestione-processo-imprese';

// Components
import { Center, StyledTipologia, StyledTitleContainer } from 'components/Layout';
import ImpresaUploadDocument from '../ImpresaUploadDocument';
import Required from '../../Forms/UI/Required';

const StyledContainer = styled.div`
  margin-left: -15px;
  margin-right: -15px;
  margin-bottom: -22px;
  margin-top: -22px;

  & table.ui.celled.table {
    border-top-right-radius: 0px;
    border-top-left-radius: 0px;
  }

  & table.ui.celled.table tr td:last-child {
    position: relative;
    height: 1px;
    padding: 0px;
  }
`;

class ImpresaDocuments extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      activeIndex: null,
      documents: [],
    };
  }

  componentDidMount() {
    this.loadDocuments();
  }

  componentDidUpdate({ isNew }) {
    if (this.props.isNew !== isNew) {
      this.loadDocuments();
    }
  }

  /** Get number of uploaded documents */
  getUploadedNumber = () => this.state.documents.filter((doc) => doc.id !== null).length;

  /** Get documents related to rete tutor  */
  loadDocuments = () => {
    if (this.props.isNew) {
      return;
    }

    GestioneProcessoImprese.getDocuments(this.props.impresaId)
      .then((res) => this.setState({ documents: res.documents }))
      .catch(() =>
        this.props.notification().addNotification({
          message:
            "Si è verificato un errore durante il recupero dei documenti. Se il problema persiste contattare l'assistenza tecnica.",
          level: 'error',
        })
      );
  };

  /** Css for documents */
  circledLabel = () => ({
    border: '1px solid #2185d0',
    background: '#ddd',
    color: '#2185d0',
    position: 'absolute',
    right: 15,
    top: '50%',
    transform: 'translateY(-50%)',
  });

  columns = (isContrattoMediazioneMissing) => [
    {
      key: 'name',
      name: 'Tipologia',
      formatter: ({ data }) => (
        <div style={{ position: 'relative', paddingLeft: 20 }}>
          <Icon
            style={{ position: 'absolute', left: -5, marginTop: -3, top: '50%', transform: 'translateY(-50%)' }}
            name="file pdf outline"
          />
          {data.name}
          <Required required={data.isRequired} />
          <div style={{ fontSize: 10, color: 'red' }}>
            {data.signingInProcess ? 'È in corso un processo di firma' : ''}
          </div>
          <div style={{ fontSize: 10, color: '#333d68' }}>
            {data.lastUpload
              ? `Ultimo upload: ${moment.unix(data.lastUpload).format('DD/MM/YYYY')} alle ${moment
                  .unix(data.lastUpload)
                  .format('HH:mm')}`
              : ''}
          </div>
        </div>
      ),
    },
    {
      key: 'uplodable',
      name: 'Caricato',
      width: 10,
      formatter: ({ data }) => (
        <Center>{data.id ? <Icon name="check" color="green" /> : <Icon name="minus" color="grey" />}</Center>
      ),
    },
    {
      key: 'action',
      name: 'Azioni',
      width: 10,
      formatter: ({ data }) => {
        const shouldDisableUpload =
          (data.name === 'Attestazione consegna precontrattuale istituto' ||
            data.name === 'Evidenza comunicazione PV istituto') &&
          isContrattoMediazioneMissing;

        return (
          <div
            style={{
              position: 'relative',
              zIndex: this.state.documents.length - _.findIndex(this.state.documents, (doc) => doc.name === data.name),
              height: '100%',
              textAlign: 'center',
              paddingTop: 11,
              paddingBottom: 11,
            }}
          >
            <ImpresaUploadDocument
              impresaId={this.props.impresaId}
              documentId={data.id}
              api={data.api}
              versions={data.versions}
              loadDocuments={this.loadDocuments}
              notification={this.props.notification}
              generate={data.generate}
              toSign={data.toSign}
              signingInProcess={data.signingInProcess}
              anagraficaId={this.props.anagraficaId}
              sbloccoGenerazioneContratto={this.props.sbloccoGenerazioneContratto}
              disabled={shouldDisableUpload}
              disabledTooltip={
                shouldDisableUpload
                  ? 'Contratto di mediazione mancante. È necessario caricare il contratto di mediazione prima di procedere con il caricamento di questo documento.'
                  : null
              }
            />
          </div>
        );
      },
    },
  ];

  /** Function to manage accordion */
  handleClick = (_e, titleProps) => {
    const { index } = titleProps;
    const { activeIndex } = this.state;
    const newIndex = activeIndex === index ? -1 : index;

    this.setState({ activeIndex: newIndex });
  };

  render() {
    const { isNew = false, anagraficaName, stato } = this.props;
    const { activeIndex } = this.state;

    /** If is a new impresa return the accordion disabled with a warning message  */
    if (isNew) {
      return (
        <span>
          <Message warning icon="warning sign" header="Effettua un primo salvataggio per attivare l'area documentale" />
          <Divider horizontal>Documentazione impresa</Divider>
          <Accordion fluid styled style={{ marginBottom: 15, backgroundColor: '#ddd' }}>
            <Accordion.Title
              active={false}
              index={0}
              style={{ position: 'relative', paddingLeft: 25, color: '#00000066' }}
            >
              <Label style={{ ...this.circledLabel(), background: '#ddd' }} circular>
                0/{this.state.documents.length}
              </Label>
              <Icon name="dropdown" />
              <StyledTitleContainer>Documentazione generale</StyledTitleContainer>
            </Accordion.Title>
          </Accordion>
        </span>
      );
    }

    /** Created Impresa */
    const isContrattoMediazioneMissing = !_.some(
      this.state.documents,
      (doc) =>
        (doc.name === 'Contratto mediazione' ||
          doc.name === 'Contratto di mediazione' ||
          doc.name === 'Contratto di mediazione creditizia Kiron') &&
        !!doc.id
    );

    return (
      <span>
        {stato === 'aperta' && (
          <Message
            warning
            icon="warning sign"
            header="Dopo il caricamento dei documenti precontrattuali e dei questionari AVC contattare settorecontrolli@kiron.it per richiedere la valutazione di competenza e la possibilità di generazione di un contratto di mediazione"
          />
        )}
        <Divider horizontal>Documentazione impresa</Divider>
        <Accordion fluid styled style={{ marginBottom: 15 }}>
          <Accordion.Title
            active={activeIndex === 0}
            index={0}
            style={{ position: 'relative', paddingLeft: 25 }}
            onClick={this.handleClick}
          >
            <Label
              style={{
                border: '1px solid #2185d0',
                background: '#fff',
                color: '#2185d0',
                position: 'absolute',
                right: 15,
                top: '50%',
                transform: 'translateY(-50%)',
              }}
              circular
            >
              {this.getUploadedNumber()}/{this.state.documents.length}
            </Label>
            <Icon name="dropdown" />
            <StyledTitleContainer>
              <StyledTipologia>Documentazione Generale</StyledTipologia>
              <div style={{ textTransform: 'uppercase' }}>{anagraficaName || 'Nominativo impresa'}</div>
            </StyledTitleContainer>
          </Accordion.Title>
          <Accordion.Content active={activeIndex === 0} className="documentBarTable">
            <StyledContainer>
              <Table
                unstackable
                className="documentTable"
                columns={this.columns(isContrattoMediazioneMissing)}
                elements={this.state.documents.map((doc) => ({ ...doc, isContrattoMediazioneMissing }))}
                canPaginate={{
                  active: false,
                }}
              />
            </StyledContainer>
          </Accordion.Content>
        </Accordion>
      </span>
    );
  }
}

ImpresaDocuments.propTypes = {
  /** If true return the document accordion disabled and a warning message */
  isNew: PropTypes.bool,
  /** Id of impresa */
  impresaId: PropTypes.string,
  /** Function for call the notification system */
  notification: PropTypes.func,
  /** Name of anagrafica */
  anagraficaName: PropTypes.string,
  /** Id of anagrafica */
  anagraficaId: PropTypes.string,
  /** State of anagrafica */
  stato: PropTypes.string,
  /** Flag sblocco generazione contratto */
  sbloccoGenerazioneContratto: PropTypes.bool,
};

export default ImpresaDocuments;



// WEBPACK FOOTER //
// ./app/components/ProcessoImpresa/ImpresaDocuments/index.js